/**
 * A-GTCC-VIEW-PSET-DZ1 — GTCC view "Platform Settings" (pset), 1:1 after
 * `docs/design/GTCC_REFERENCE_2026-09-01.html`, `div[data-v="pset"]`
 * (lines 849-869).
 *
 * Layout of the reference, element for element:
 *   - head "Platform Settings" with the freshness badge and the `.sub` line
 *     "Feature-Flags und Plattform-Konfiguration — jede Änderung landet im
 *     Audit Log." (the reference titles the standalone page with `h1`; inside
 *     the deck shell that `h1` belongs to the page frame, so the view titles
 *     itself `h2`, exactly like `views/ovw/OvwView.tsx`),
 *   - `.grid-main` panel "Feature-Flags": one row per flag, name in `.mono`,
 *     state "AN" / "wartet auf Schlüssel" / "AUS (<Grund>)",
 *   - panel "Plattform": Session-Dauer Web, Sprachen, Konto-Löschfrist,
 *   - panel "Aus der alten Konsole übernommen": three rows that jump to the
 *     views which own those settings today (`cat`, `brand`, `supin`).
 *
 * Deliberate deviations from the reference, each measured rather than chosen:
 *   - the flag rows are READ-ONLY. Switching a flag is not a path this view
 *     may open: the only existing write in the settings area is the
 *     visibility mode (`views/settings/useGtccSettings.ts:…useSetGtccVisibilityMode`),
 *     there is no flag-toggle endpoint at all, and the instruction forbids a
 *     new writing path. The panel therefore names where a flag is changed
 *     instead of pretending it can change it here,
 *   - the reference's flag reasons ("bis App-Release", "E2, Texte → Owner")
 *     are sample text; the reason printed next to "AUS" is the flag's own
 *     `description` from `/v1/feature-flags`, unchanged,
 *   - the open-request count is capped by the inbox route at
 *     {@link PSET_SUPPORT_OPEN_LIMIT}; at the cap the row reads "mindestens
 *     N", because N is then a floor and not a measurement.
 *
 * Honesty (Owner P8/P9/P10): no number is computed here and no clock is read.
 * A pending, failed or forbidden source renders its own failure text — never a
 * zero, never the reference's sample figures.
 */
import { ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { RTL_LOCALES, SUPPORTED_LOCALES, type Locale } from "@genius-tracker/i18n";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, type ReactNode } from "react";

import {
  PSET_FEATURE_FLAGS_KEY,
  PSET_PLATFORM_KEY,
  PSET_SUPPORT_OPEN_KEY,
  PSET_SUPPORT_OPEN_LIMIT,
  fetchPsetFeatureFlags,
  fetchPsetOpenSupportTickets,
  fetchPsetPlatform,
  psetFlagState,
  type PsetFeatureFlagsDto,
  type PsetPlatformDto,
  type PsetSupportTicketsDto,
} from "../../api/pset.js";
import type { GtccResult } from "../../api/gtcc-result.js";
import { writeGtccViewKeyToLocation } from "../../deep-link-url.js"; // Blatt-Modul: bricht den Zyklus registry -> View -> deep-link -> registry.
import { FreshBadge } from "../../ui/primitives.js";
import {
  ControlActionRow,
  ControlHead,
  ControlMono,
  ControlNote,
  ControlPanel,
  ControlRow,
  GRID_MAIN_CLASS,
  formatInteger,
} from "../control/ControlPrimitives.js";

/* --------------------------------------------------------------- queries */

/** Same floor as every other GTCC view: never refetch faster than 30 s. */
const PSET_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: false,
} as const;

/** Minutes per day — the unit the reference writes the two durations in. */
const MINUTES_PER_DAY = 1_440;

/* ----------------------------------------------------------------- gate */

/**
 * One gate per panel: the three sources sit behind different permissions
 * (`/v1/feature-flags` needs none, the platform block and the inbox need the
 * platform owner), so a single page-level error would hide two panels that
 * answered perfectly well.
 */
function PsetGate<T>(props: {
  result: GtccResult<T> | undefined;
  isError: boolean;
  testId: string;
  render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { result } = props;

  if (result !== undefined && result.ok) return props.render(result.data);

  /* A rejected query is the one failure `gtccGet` cannot describe: it never
     got far enough to answer. It is still a failure and is shown as one —
     leaving it as "loading" would spin forever. */
  const failure =
    result !== undefined
      ? result
      : props.isError
        ? ({ ok: false, kind: "transport_error", message: "" } as const)
        : null;

  if (failure === null) {
    return (
      <div data-testid={`${props.testId}-loading`}>
        <LoadingState label={t("admin.gtcc.pset.loading")} />
      </div>
    );
  }

  const body =
    failure.kind === "disabled"
      ? t("admin.gtcc.pset.failure.disabled")
      : failure.kind === "forbidden"
        ? t("admin.gtcc.pset.failure.forbidden")
        : failure.kind === "upstream"
          ? t("admin.gtcc.pset.failure.upstream", { status: failure.httpStatus })
          : t("admin.gtcc.pset.failure.transport");

  return (
    <div data-testid={`${props.testId}-failure`}>
      <ErrorState severity="warn" title={t("admin.gtcc.pset.failure.title")} body={body} />
    </div>
  );
}

/* ------------------------------------------------------------ languages */

/**
 * "DE · EN · FR · AR (RTL)" of the reference, built from the shipped locale
 * list instead of typed out: `SUPPORTED_LOCALES` IS the platform's language
 * configuration, so a locale added to the package appears here by itself.
 *
 * Order: the locale currently being read first, then the remaining
 * left-to-right locales in their declared order, then the right-to-left ones —
 * which reads the reference's "DE · EN · FR · AR (RTL)" on the German sheet
 * and, unlike a hand-written list, survives the next locale.
 */
export function psetLanguageList(
  locales: readonly Locale[],
  rtl: ReadonlySet<Locale>,
  rtlSuffix: (code: string) => string,
  active: Locale,
): string {
  const ltr = locales.filter((locale) => !rtl.has(locale));
  const ordered = ltr.includes(active)
    ? [active, ...ltr.filter((locale) => locale !== active)]
    : ltr;
  const rightToLeft = locales.filter((locale) => rtl.has(locale));
  return [
    ...ordered.map((locale) => locale.toUpperCase()),
    ...rightToLeft.map((locale) => rtlSuffix(locale.toUpperCase())),
  ].join(" · ");
}

/* --------------------------------------------------------------- panels */

function FlagsPanel(props: { data: PsetFeatureFlagsDto }): ReactNode {
  const { t } = useLocale();
  /* Eine Antwort ohne `flags` ist kein Grund, die Konsole weiss zu faerben:
     die Liste bleibt dann einfach leer. */
  const flags = Array.isArray(props.data.flags) ? props.data.flags : [];
  if (flags.length === 0) {
    return (
      <Stack gap={0} data-testid="gtcc-pset-flags">
        <ControlNote testId="gtcc-pset-flags-note">{t("admin.gtcc.pset.flags.note")}</ControlNote>
      </Stack>
    );
  }
  return (
    <Stack gap={0} data-testid="gtcc-pset-flags">
      {flags.map((flag, index) => {
        const state = psetFlagState(flag);
        return (
          <ControlRow
            key={flag.key}
            testId={`gtcc-pset-flag-${flag.key}`}
            tone={state === "on" ? "ok" : "info"}
            /* Die Referenz (Zeile 853) schreibt den Flag-Namen als Flaeche,
               nicht in Festbreitenschrift — `.mono` traegt dort nur der
               nachlaufende Zustand. Und sie haengt die Welle hinter dem
               Schluessel nur beim ersten Eintrag an ("GEOCODE_REVERSE (MA)");
               die uebrigen Eintraege bleiben beim blossen Schluessel — die
               Welle ist ein gelesener Wert der Antwort (`wave`), keine
               Erfindung, aber der Suffix ist es nur einmal wert. */
            label={
              index === 0 && typeof flag.wave === "string" && flag.wave !== ""
                ? `${flag.key} (${flag.wave})`
                : flag.key
            }
            value={
              <ControlMono testId={`gtcc-pset-flag-state-${flag.key}`}>
                {state === "on"
                  ? t("admin.gtcc.pset.flags.state.on")
                  : state === "awaiting_key"
                    ? t("admin.gtcc.pset.flags.state.awaiting_key")
                    : t("admin.gtcc.pset.flags.state.off", { reason: flag.description })}
              </ControlMono>
            }
          />
        );
      })}
    </Stack>
  );
}

function PlatformPanel(props: { data: PsetPlatformDto }): ReactNode {
  const { t, locale } = useLocale();
  const { data } = props;

  const sessionValue =
    data.sessionAbsoluteCapMinutes % MINUTES_PER_DAY === 0
      ? t("admin.gtcc.pset.platform.days", {
          days: formatInteger(data.sessionAbsoluteCapMinutes / MINUTES_PER_DAY, locale),
        })
      : t("admin.gtcc.pset.platform.minutes", {
          minutes: formatInteger(data.sessionAbsoluteCapMinutes, locale),
        });

  return (
    <Stack gap={0} data-testid="gtcc-pset-platform">
      <ControlRow
        testId="gtcc-pset-platform-session"
        tone="ok"
        label={t("admin.gtcc.pset.platform.session")}
        value={<ControlMono testId="gtcc-pset-platform-session-value">{sessionValue}</ControlMono>}
      />
      <ControlRow
        testId="gtcc-pset-platform-languages"
        tone="ok"
        label={t("admin.gtcc.pset.platform.languages")}
        value={
          <ControlMono testId="gtcc-pset-platform-languages-value">
            {psetLanguageList(
              [...SUPPORTED_LOCALES],
              RTL_LOCALES,
              (code) => t("admin.gtcc.pset.platform.language_rtl", { code }),
              locale,
            )}
          </ControlMono>
        }
      />
      <ControlRow
        testId="gtcc-pset-platform-deletion"
        tone="ok"
        label={t("admin.gtcc.pset.platform.deletion")}
        value={
          <ControlMono testId="gtcc-pset-platform-deletion-value">
            {t("admin.gtcc.pset.platform.days", {
              days: formatInteger(data.accountDeletionGraceDays, locale),
            })}
          </ControlMono>
        }
      />
    </Stack>
  );
}

/* ------------------------------------------------------------------ view */

export function PsetView(): ReactNode {
  const { t, locale } = useLocale();

  const platformQuery = useQuery({
    queryKey: PSET_PLATFORM_KEY,
    queryFn: ({ signal }) => fetchPsetPlatform(signal),
    ...PSET_QUERY_OPTIONS,
  });
  const flagsQuery = useQuery({
    queryKey: PSET_FEATURE_FLAGS_KEY,
    queryFn: ({ signal }) => fetchPsetFeatureFlags(signal),
    ...PSET_QUERY_OPTIONS,
  });
  const supportQuery = useQuery({
    queryKey: PSET_SUPPORT_OPEN_KEY,
    queryFn: ({ signal }) => fetchPsetOpenSupportTickets(signal),
    ...PSET_QUERY_OPTIONS,
  });

  const platform =
    platformQuery.data !== undefined && platformQuery.data.ok ? platformQuery.data.data : null;

  const goTo = useCallback((key: string) => {
    writeGtccViewKeyToLocation(key, false, import.meta.env.BASE_URL);
    if (typeof window !== "undefined") window.dispatchEvent(new PopStateEvent("popstate"));
  }, []);

  /* The platform block is the only real timestamp this view has. */
  const measuredAt = platform === null ? null : new Date(platform.measuredAt);
  return (
    <Stack gap={3} data-testid="gtcc-view-pset">
      <ControlHead
        testId="gtcc-pset-head"
        title={t("admin.gtcc.pset.head.title")}
        subtitle={t("admin.gtcc.pset.head.subtitle")}
        fresh={
          <span data-testid="gtcc-pset-fresh">
            <FreshBadge measuredAt={measuredAt} stale={platform === null} />
          </span>
        }
      />

      <div className={GRID_MAIN_CLASS}>
        <ControlPanel testId="gtcc-pset-flags-panel" title={t("admin.gtcc.pset.flags.title")}>
          <PsetGate
            result={flagsQuery.data}
            isError={flagsQuery.isError}
            testId="gtcc-pset-flags"
            render={(data: PsetFeatureFlagsDto) => <FlagsPanel data={data} />}
          />
        </ControlPanel>

        <ControlPanel testId="gtcc-pset-platform-panel" title={t("admin.gtcc.pset.platform.title")}>
          <PsetGate
            result={platformQuery.data}
            isError={platformQuery.isError}
            testId="gtcc-pset-platform"
            render={(data: PsetPlatformDto) => <PlatformPanel data={data} />}
          />
        </ControlPanel>

        <ControlPanel testId="gtcc-pset-console-panel" title={t("admin.gtcc.pset.console.title")}>
          <Stack gap={0} data-testid="gtcc-pset-console">
            <ControlActionRow
              testId="gtcc-pset-console-entitlements"
              tone="info"
              label={t("admin.gtcc.pset.console.entitlements")}
              value={<ControlMono>{t("admin.gtcc.pset.console.entitlements_hint")}</ControlMono>}
              onActivate={() => goTo("cat")}
            />
            <ControlActionRow
              testId="gtcc-pset-console-branding"
              tone="info"
              label={t("admin.gtcc.pset.console.branding")}
              value={<ControlMono>{t("admin.gtcc.pset.console.branding_hint")}</ControlMono>}
              onActivate={() => goTo("brand")}
            />
            <PsetGate
              result={supportQuery.data}
              isError={supportQuery.isError}
              testId="gtcc-pset-console-support"
              render={(data: PsetSupportTicketsDto) => {
                /* Eine Antwort ohne `items` zaehlt als null offene Tickets,
                   statt die ganze Konsole in die Fehlergrenze zu reissen —
                   dieselbe Haltung wie bei den Feature-Flags oben. */
                const count = Array.isArray(data.items) ? data.items.length : 0;
                const capped = count >= PSET_SUPPORT_OPEN_LIMIT;
                return (
                  <ControlActionRow
                    testId="gtcc-pset-console-support-row"
                    tone="info"
                    label={t("admin.gtcc.pset.console.support")}
                    value={
                      <ControlMono testId="gtcc-pset-console-support-count">
                        {t(
                          capped
                            ? "admin.gtcc.pset.console.support_open_at_least"
                            : "admin.gtcc.pset.console.support_open",
                          { count: formatInteger(count, locale) },
                        )}
                      </ControlMono>
                    }
                    onActivate={() => goTo("supin")}
                  />
                );
              }}
            />
          </Stack>
        </ControlPanel>
      </div>
    </Stack>
  );
}
